import { PlusIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import type { Color } from "@/lib/types";
import { cn } from "@/lib/utils";
import { ColorList } from "./properties/SelectEditor";
import { Popover } from "./ui/Popover";

/** Building blocks shared by the settings and finance pages. */

export function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="text-sm text-muted">{description}</p>}
      </div>
      {children}
    </section>
  );
}

export function ColorDot({ color, onChange, disabled }: { color: Color; onChange: (c: Color) => void; disabled?: boolean }) {
  return (
    <Popover
      disabled={disabled}
      className="w-44 p-1"
      trigger={(t) => (
        <button ref={t.ref} onClick={t.onClick} type="button" disabled={disabled} className={cn("size-5 shrink-0 rounded", `tag-${color}`)} aria-label="Change color" />
      )}
    >
      {(close) => (
        <ColorList
          value={color}
          onChange={(c) => {
            onChange(c);
            close();
          }}
        />
      )}
    </Popover>
  );
}

export function InlineName({ value, onSave, disabled, className }: { value: string; onSave: (v: string) => void; disabled?: boolean; className?: string }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => setDraft(value), [value]);
  return (
    <input
      value={draft}
      readOnly={disabled}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => draft.trim() && draft.trim() !== value && onSave(draft.trim())}
      onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
      className={cn("h-8 min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-2 text-sm outline-none", !disabled && "hover:border-line focus:border-accent", className)}
      dir="auto"
    />
  );
}

export function AddRow({ placeholder, onAdd, disabled }: { placeholder: string; onAdd: (name: string) => Promise<unknown>; disabled?: boolean }) {
  const [name, setName] = useState("");
  if (disabled) return null;
  return (
    <form
      className="flex items-center gap-2 ps-7"
      onSubmit={async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        await onAdd(name.trim());
        setName("");
      }}
    >
      <PlusIcon className="size-4 text-faint" />
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder={placeholder} className="h-8 flex-1 bg-transparent text-sm outline-none placeholder:text-faint" dir="auto" />
    </form>
  );
}
